import React from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { Grid } from '@material-ui/core';
import DataUnavailable from 'components/RabbitOKRSidePanel/Components/DataUnavailable';
import { Loader } from 'screens/analytics/commonComponents';
import styled from 'styled-components';
import {
  GutterCSS,
  RelativePositionFullHeightContainer,
  StyledDivider,
  VerticallyCenterContainer,
} from 'styles/common';

import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { FEEDBACK_FORM_VISIBILITY, SHARE_FEEDBACK_TYPES } from 'constants/ProjectConstants';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TextParseComponent } from 'uiToolkitV1/TextParseComponent';
import { TextBodyLarge, TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { DrawerKebabMenu } from './common/DrawerKebabMenu';
import { useSharedOrReceivedFeedBackData } from './hooks/dataHooks';

function getTypeData(type) {
  switch (type) {
    case 'APPRECIATIVE':
      return '#1A5724';
    case 'CONSTRUCTIVE':
      return '#B96400';
    default:
      return null;
  }
}

const SectionContainer = styled.div`
  margin: 0.5rem 0 2rem 0;
  ${GutterCSS}
`;

const StyledBody = styled.div`
  padding: 16px;
  width: 100%;
`;

const StyledGrid = styled(Grid)`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Container = styled.div`
  background: linear-gradient(92.27deg, rgba(250, 250, 250, 0.2) 27.36%, rgba(242, 255, 238, 0) 150.63%);
  border: 1px solid #f2f2f2;
`;

const ProviderLeftComp = (data) => () => (
  <Avatar name={data?.providerEmployee?.displayName} src={data?.providerEmployee?.profilePhoto} />
);

const VisibilityLeftComp = (continuousFeedback) => () => (
  <div>{FEEDBACK_FORM_VISIBILITY?.find((item) => item.value === continuousFeedback?.visibility)?.icon}</div>
);

const ObjectiveLeftComp = () => <Objective color="functional.7651BC" />;

function SharedOrReceivedFullCommentDrawer({ postId, formType, handleClose }) {
  const { data, isLoading } = useSharedOrReceivedFeedBackData({ postId });
  const { continuousFeedback, canDelete } = data ?? {};
  const { feedbackPost } = continuousFeedback ?? {};
  const { macros, id: feedbackId } = feedbackPost ?? {};

  if (isLoading) {
    return <Loader />;
  }

  return (
    <RelativePositionFullHeightContainer>
      <Container
        style={{
          background:
            macros?.attributes?.sentiment === 'APPRECIATIVE'
              ? 'radial-gradient(71.8% 134.17% at 50% 50%, #FFFFFF 0%, #F9FFF8 100%)'
              : 'rgba(253, 246, 228, 0.1)',
        }}
      >
        {macros?.attributes?.sentiment && (
          <StyledDivider
            variant="fullWidth"
            style={{
              backgroundColor: macros?.attributes?.sentiment === 'APPRECIATIVE' ? '#DBF2D3' : '#FDF6E4',
              height: '6px',
            }}
            thickness={1}
          />
        )}
        {!data ? (
          <DataUnavailable />
        ) : (
          <StyledBody>
            <Flex justifyContent="space-between">
              <SectionContainer>
                <TextLabel
                  sx={{
                    mb: 2,
                  }}
                >
                  {formType === 'SHOW_PROVIDED' ? 'Provided By' : 'Received From'}
                </TextLabel>

                <ListItemComp
                  withoutBackground
                  title={data?.providerEmployee?.displayName}
                  LeftComponent={ProviderLeftComp(data)}
                />
              </SectionContainer>
              {canDelete && <DrawerKebabMenu feedbackId={feedbackId} handleClose={handleClose} />}
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
              <Grid item sm={5}>
                <TextLabel>Type</TextLabel>
                <VerticallyCenterContainer>
                  <TextBodyMedium bold color={getTypeData(macros?.attributes?.sentiment)}>
                    {SHARE_FEEDBACK_TYPES.find((item) => item.value === macros?.attributes?.sentiment)?.label}
                  </TextBodyMedium>
                </VerticallyCenterContainer>
              </Grid>
              <StyledGrid item sm={6}>
                <TextLabel>Date</TextLabel>
                <VerticallyCenterContainer>
                  {feedbackPost?.createdTS && <DateComp date={feedbackPost?.createdTS} color="black" />}
                </VerticallyCenterContainer>
              </StyledGrid>
              <Grid item sm={5}>
                <TextLabel>Visibility</TextLabel>
                <ListItemComp
                  isBoldText={false}
                  withoutBackground
                  title={FEEDBACK_FORM_VISIBILITY?.find((item) => item.value === continuousFeedback?.visibility)?.label}
                  LeftComponent={VisibilityLeftComp(continuousFeedback)}
                />
              </Grid>
            </Flex>
            <Box mt={2}>
              <TextLabel mt={6} mb={1}>
                Feedback Comment
              </TextLabel>
              <Box mr={5}>
                <TextParseComponent text={feedbackPost?.text} macros={macros} />
              </Box>

              {feedbackPost?.media?.[0]?.url && (
                <Image
                  sx={{
                    maxWidth: '100%', // <- using max-width instead of width, cause incase of small sized images it was stretching.
                    padding: '20px',
                    objectFit: 'cover',
                  }}
                  src={feedbackPost?.media?.[0]?.url}
                  alt=""
                />
              )}
            </Box>
            {continuousFeedback?.goal && (
              <Box mt={8}>
                <TextLabel>Related Goal</TextLabel>
                <ListItemComp
                  withoutBackground
                  title={<TextBodyLarge>{continuousFeedback?.goal?.text}</TextBodyLarge>}
                  LeftComponent={ObjectiveLeftComp}
                />
              </Box>
            )}
          </StyledBody>
        )}
      </Container>
    </RelativePositionFullHeightContainer>
  );
}

export default SharedOrReceivedFullCommentDrawer;
